/**
 * Shared aerial framing rule for every report export.
 *
 * Reports used to frame the property differently per export path (percentage
 * padding here, a fixed ground span there), so the same lot could appear small
 * in one PDF and tight in another. Every aerial now uses one rule: the lot
 * bounds plus a fixed 50 ft buffer beyond the lot lines, expanded only as much
 * as the output aspect ratio requires.
 */

/** Default buffer printed around the lot lines, in feet.
 *  Kept deliberately tight so the lot fills the frame with just enough
 *  surrounding context to read the frontage. */
export const AERIAL_BUFFER_FEET = 25;
/** Same buffer in meters. */
export const AERIAL_BUFFER_M = AERIAL_BUFFER_FEET * 0.3048;
/** Adjustable range for the buffer, in feet. */
export const AERIAL_BUFFER_MIN_FEET = 0;
export const AERIAL_BUFFER_MAX_FEET = 300;

const BUFFER_KEY = "easements:aerial-buffer-feet";

/** Clamps any user-supplied buffer to the supported range. */
export function clampBufferFeet(feet: number | undefined | null): number {
  if (!Number.isFinite(feet as number)) return AERIAL_BUFFER_FEET;
  return Math.min(
    Math.max(Math.round(feet as number), AERIAL_BUFFER_MIN_FEET),
    AERIAL_BUFFER_MAX_FEET,
  );
}

/**
 * Buffer used by every report aerial. Persisted so PDFs, PNGs, diagrams and
 * the enhanced site plan all crop the property identically.
 */
export function getAerialBufferFeet(): number {
  if (typeof window === "undefined") return AERIAL_BUFFER_FEET;
  const raw = window.localStorage.getItem(BUFFER_KEY);
  return raw === null ? AERIAL_BUFFER_FEET : clampBufferFeet(Number(raw));
}

export function setAerialBufferFeet(feet: number): number {
  const next = clampBufferFeet(feet);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(BUFFER_KEY, String(next));
  }
  return next;
}
/** Frame used when no boundary geometry is available (pin-only reports). */
export const AERIAL_PIN_SPAN_M = 45;

const M_PER_DEG_LAT = 110_540;

export interface AerialBbox {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

function metersPerDegLng(lat: number) {
  return 111_320 * Math.max(Math.cos((lat * Math.PI) / 180), 0.2);
}

/** Bounds of every supplied [lat,lng] ring, or null when there is none. */
export function ringsExtent(ringSets: Array<Array<Array<[number, number]>>>): AerialBbox | null {
  const pts = ringSets
    .flat(2)
    .filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (!pts.length) return null;
  const lats = pts.map((p) => p[0]);
  const lngs = pts.map((p) => p[1]);
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
  };
}

/**
 * The tightest sensible frame for a property: lot bounds + 50 ft, then grown
 * on one axis only to match `aspect` (width / height of the output image).
 *
 * Falls back to a small square around `center` when there is no geometry.
 */
export function aerialFrame(args: {
  center: [number, number];
  ringSets: Array<Array<Array<[number, number]>>>;
  aspect: number;
  /** Override the pin-only span (meters). */
  pinSpanMeters?: number;
  /** Override the buffer beyond the lot lines (feet). Defaults to the saved setting. */
  bufferFeet?: number;
}): AerialBbox {
  const { center, ringSets, aspect } = args;
  const bufferM = clampBufferFeet(args.bufferFeet ?? getAerialBufferFeet()) * 0.3048;
  const extent = ringsExtent(ringSets);

  const cLat = extent ? (extent.minLat + extent.maxLat) / 2 : center[0];
  const cLng = extent ? (extent.minLng + extent.maxLng) / 2 : center[1];
  const mLng = metersPerDegLng(cLat);

  const pinSpan = args.pinSpanMeters ?? AERIAL_PIN_SPAN_M;

  // Ground spans in meters, buffered by 50 ft on every side.
  let spanLatM = extent ? (extent.maxLat - extent.minLat) * M_PER_DEG_LAT + bufferM * 2 : pinSpan;
  let spanLngM = extent ? (extent.maxLng - extent.minLng) * mLng + bufferM * 2 : pinSpan;

  // Guard degenerate geometry (single point / hairline rings). Keep the crop
  // usable even at a 0 ft buffer.
  const floor = Math.max(bufferM * 2, 12);
  spanLatM = Math.max(spanLatM, floor);
  spanLngM = Math.max(spanLngM, floor);

  // Grow the short axis only, so the lot is never cropped or squashed.
  if (spanLngM / spanLatM < aspect) spanLngM = spanLatM * aspect;
  else spanLatM = spanLngM / aspect;

  const halfLat = spanLatM / 2 / M_PER_DEG_LAT;
  const halfLng = spanLngM / 2 / mLng;

  return {
    minLat: cLat - halfLat,
    maxLat: cLat + halfLat,
    minLng: cLng - halfLng,
    maxLng: cLng + halfLng,
  };
}

/** Feet across the shorter and longer ground axis of a set of rings. */
export function lotSpanFeet(
  ringSets: Array<Array<Array<[number, number]>>>,
): { shortFeet: number; longFeet: number } | null {
  const extent = ringsExtent(ringSets);
  if (!extent) return null;
  const cLat = (extent.minLat + extent.maxLat) / 2;
  const latFt = ((extent.maxLat - extent.minLat) * M_PER_DEG_LAT) / 0.3048;
  const lngFt = ((extent.maxLng - extent.minLng) * metersPerDegLng(cLat)) / 0.3048;
  return { shortFeet: Math.min(latFt, lngFt), longFeet: Math.max(latFt, lngFt) };
}

export interface BufferRecommendation {
  feet: number;
  /** Plain-English reason, shown next to the Auto control. */
  reason: string;
  lotShortFeet: number;
  lotLongFeet: number;
}

/**
 * Suggests the tightest buffer that still shows enough context for the lot.
 *
 * The rule is proportional — 12% of the lot's short axis — so a 40 ft urban
 * infill lot gets a tight crop while an acre-plus parcel still shows the
 * frontage and adjoining rights-of-way. It is then clamped so the buffer is
 * never so thin it disappears at the export scale (at least ~8px of the
 * output image) and never so wide it shrinks the lot in the frame.
 */
export function recommendAerialBufferFeet(args: {
  ringSets: Array<Array<Array<[number, number]>>>;
  /** Width of the exported image, in pixels. Used to keep the margin visible. */
  outputWidthPx?: number;
}): BufferRecommendation | null {
  const span = lotSpanFeet(args.ringSets);
  if (!span || span.longFeet <= 0) return null;

  const proportional = span.shortFeet * 0.12;
  // Never wider than a quarter of the lot, so the parcel stays dominant.
  const contextCap = Math.max(span.longFeet * 0.25, 15);

  let feet = Math.min(Math.max(proportional, 15), contextCap);
  let reason =
    span.shortFeet < 80
      ? "Tight crop for a small lot."
      : span.shortFeet > 300
        ? "Wider margin so a large parcel still shows its frontage."
        : "Balanced margin for this lot size.";

  // Keep the margin legible at the export scale: a buffer thinner than ~8px
  // of the finished image reads as no margin at all.
  const widthPx = args.outputWidthPx ?? 0;
  if (widthPx > 0) {
    const frameFeet = span.longFeet + feet * 2;
    const pxPerFoot = widthPx / frameFeet;
    const minVisibleFeet = 8 / Math.max(pxPerFoot, 1e-6);
    if (minVisibleFeet > feet) {
      feet = minVisibleFeet;
      reason = "Widened slightly so the margin is visible at this map scale.";
    }
  }

  // Round to the slider's 5 ft step, staying inside the supported range.
  const rounded = clampBufferFeet(Math.round(feet / 5) * 5);
  return {
    feet: Math.max(rounded, 5),
    reason,
    lotShortFeet: Math.round(span.shortFeet),
    lotLongFeet: Math.round(span.longFeet),
  };
}
